import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import SimpleCandidateInfoSection from "@/components/SimpleCandidateInfoSection";
import { JobList } from "@/components/jobs/JobList";
import { useAIJobSearch } from "@/hooks/useAIJobSearch";
import { useJobs } from "@/hooks/useJobs";
import { JobSitemap } from "@/components/JobSitemap";
import { SEOPageMeta } from "@/components/SEOPageMeta";
import { SEOBreadcrumbSchema } from "@/components/SEOBreadcrumbSchema";
import { ChevronRight, Search } from "lucide-react";
import { GradientHeading } from "@/components/ui/gradient-heading";
import { Job } from "@/types/job";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const QUICK_QUERIES = [
  'Jobs in München',
  'Gastronomie-Jobs',
  'Vollzeit-Stellen',
  'Führungspositionen',
];

const Candidates = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const aiMatchedJobsFromState = (location.state?.aiMatchedJobs as Job[]) || null;
  const searchQuery = (location.state?.searchQuery as string) || '';
  const showAISearchFromState = location.state?.showAISearch || false;
  const [showingAIResults, setShowingAIResults] = useState(!!aiMatchedJobsFromState);
  const [query, setQuery] = useState('');
  const { searchJobs, isSearching, reasoning, confidence } = useAIJobSearch();
  const { allJobs } = useJobs({});
  
  useEffect(() => {
    // Scroll to filters if coming from job detail page or AI search
    if (location.state?.scrollToFilters || aiMatchedJobsFromState) {
      setTimeout(() => {
        const filtersElement = document.getElementById('job-filters');
        if (filtersElement) {
          filtersElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location, aiMatchedJobsFromState]);

  const scrollToJobFilters = () => {
    const filtersElement = document.getElementById('job-filters');
    if (filtersElement) {
      filtersElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSearch = async () => {
    if (query.trim()) {
      const results = await searchJobs(allJobs, query);
      if (results && results.length > 0) {
        navigate('/bewerbende', { 
          state: { 
            aiMatchedJobs: results,
            searchQuery: query,
            scrollToFilters: true 
          },
          replace: true
        });
        setShowingAIResults(true);
      }
    }
  };

  const handleQuickQuery = async (quickQuery: string) => {
    setQuery(quickQuery);
    const results = await searchJobs(allJobs, quickQuery);
    if (results && results.length > 0) {
      navigate('/bewerbende', { 
        state: { 
          aiMatchedJobs: results,
          searchQuery: quickQuery,
          scrollToFilters: true 
        },
        replace: true
      });
      setShowingAIResults(true);
    }
  };

  return (
    <>
      <SEOPageMeta 
        title="Stellenangebote Hotellerie & Gastronomie"
        description="Aktuelle Jobs in Hotellerie & Gastronomie. Exklusive Führungspositionen bei Top-Unternehmen. Finden Sie Ihren Traumjob mit Scholz Interim. Jetzt bewerben!"
        path="/bewerbende"
        keywords={["Jobs Hotellerie", "Stellenangebote Gastronomie", "Karriere Tourismus", "Führungspositionen", "Job Hotelmanagement"]}
      />
      <SEOBreadcrumbSchema items={[
        { name: "Home", path: "/" },
        { name: "Stellenangebote", path: "/bewerbende" }
      ]} />
      <Header />
      
      {/* Enhanced Hero Section with Integrated Search */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Enhanced Background with Depth */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-elegant opacity-90" />
          
          {/* Layered Background Elements - Mobile Responsive */}
          <div className="absolute top-1/4 right-1/4 w-48 h-48 sm:w-96 sm:h-96 rounded-full blur-3xl opacity-40" style={{
            background: 'radial-gradient(circle, hsl(210 65% 32% / 0.1) 0%, transparent 70%)'
          }} />
          <div className="absolute bottom-1/3 left-1/3 w-32 h-32 sm:w-64 sm:h-64 rounded-full blur-2xl opacity-30" style={{
            background: 'radial-gradient(circle, hsl(210 65% 32% / 0.08) 0%, transparent 70%)'
          }} />
          
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.015]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(210 65% 32%) 1px, transparent 0)`,
            backgroundSize: '30px 30px'
          }} />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-8 text-center">
          <div className="max-w-4xl mx-auto animate-fade-in">
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <span className="w-12 sm:w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></span>
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{
                  backgroundColor: 'hsl(210 65% 32%)'
                }}></div>
                <span className="w-12 sm:w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></span>
              </div>
              <p className="font-medium tracking-wider uppercase text-xs sm:text-sm mb-4 sm:mb-6" style={{
                color: 'hsl(210 65% 32%)'
              }}>FÜR INTERESSIERTE</p>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-7xl font-display font-extralight mb-6 sm:mb-8 leading-tight px-4 sm:px-0">
              <span className="bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent">
                Offene Stellen
              </span>
              <span className="block text-2xl sm:text-4xl md:text-5xl font-light mt-2 sm:mt-4" style={{
                color: 'hsl(210 65% 32%)'
              }}>
                & Karrierechancen
              </span>
            </h1>
            
            <div className="max-w-3xl mx-auto mb-8 sm:mb-12 relative px-4 sm:px-0">
              <div className="hidden sm:block absolute -left-6 top-0 w-1 h-full bg-gradient-to-b from-transparent via-primary to-transparent opacity-30"></div>
              <p className="text-base sm:text-xl text-muted-foreground leading-relaxed font-light">
                Entdecken Sie exklusive Positionen in der Hotellerie und Gastronomie. 
                Finden Sie Ihren Traumjob bei führenden Unternehmen der Branche.
              </p>
            </div>
            
            {/* Smarte Jobsuche - Mobile Optimiert */}
            <div className="max-w-3xl mx-auto px-4 sm:px-0">
              <div className="glass-effect bg-card/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-primary/30 shadow-2xl p-4 sm:p-6 lg:p-8">
                <div className="mb-4 sm:mb-6 text-center">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-display font-light mb-2">
                    Smarte <span className="font-medium" style={{ color: 'hsl(210 65% 32%)' }}>Jobsuche</span>
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground px-2">
                    Stellen Sie eine Frage in natürlicher Sprache
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mb-3 sm:mb-4">
                  <Input
                    placeholder="z.B. 'Jobs in München'"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    disabled={isSearching}
                    className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                  />
                  <Button 
                    onClick={handleSearch}
                    disabled={isSearching || !query.trim()}
                    size="lg"
                    className="w-full sm:w-auto h-11 sm:h-12 px-6"
                  >
                    <Search className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                    <span>Suchen</span>
                  </Button>
                </div>

                {/* Mobile-optimierte Quick Queries */}
                <div className="space-y-2 mb-4">
                  <p className="text-xs text-muted-foreground text-center sm:text-left">Schnellsuche:</p>
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    {QUICK_QUERIES.map((quickQuery) => (
                      <Badge
                        key={quickQuery}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary/10 active:bg-primary/20 transition-colors text-xs py-2 px-3 justify-center whitespace-nowrap"
                        onClick={() => handleQuickQuery(quickQuery)}
                      >
                        {quickQuery}
                      </Badge>
                    ))}
                  </div>
                </div>

                {isSearching && (
                  <div className="flex items-center justify-center py-6 mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                      <span className="text-xs sm:text-sm text-muted-foreground">Analysiere {allJobs.length} Jobs...</span>
                    </div>
                  </div>
                )}

                {reasoning && !isSearching && (
                  <div className="mt-4 p-3 sm:p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm font-medium mb-1">Analyse</p>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{reasoning}</p>
                        {confidence !== undefined && confidence > 0 && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">Übereinstimmung:</span>
                            <div className="flex-1 h-2 bg-secondary/20 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary transition-all duration-300"
                                style={{ width: `${confidence}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium whitespace-nowrap">{confidence}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Alle Jobs Button */}
                <div className="mt-4 pt-4 border-t border-border/20 flex justify-center">
                  <Button
                    onClick={scrollToJobFilters}
                    variant="outline"
                    className="gap-2"
                  >
                    Alle offenen Stellen anzeigen
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {showingAIResults && (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-center">
                      <Button
                        onClick={() => {
                          setShowingAIResults(false);
                          navigate('/bewerbende', { replace: true });
                        }}
                        variant="outline"
                        className="gap-2"
                      >
                        Alle Jobs anzeigen
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex justify-center">
                      <Button
                        onClick={() => {
                          setQuery('');
                          setShowingAIResults(false);
                          navigate('/bewerbende', { 
                            state: { showAISearch: true },
                            replace: true 
                          });
                        }}
                        variant="outline"
                        className="gap-2"
                      >
                        <Search className="h-4 w-4" />
                        Neue KI-Suche starten
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Bottom Shape */}
        <div className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 bg-background organic-shape">
          <div className="absolute inset-0 bg-gradient-accent-subtle opacity-50"></div>
        </div>
      </section>
      
      {/* Simple Candidate Info */}
      <SimpleCandidateInfoSection />
      
      <main className="min-h-screen py-4 sm:py-8 bg-background">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="glass-effect bg-card/90 backdrop-blur-sm rounded-lg sm:rounded-xl lg:rounded-2xl border border-border/30 overflow-hidden mb-6 sm:mb-8" id="job-filters">
              <div className="p-4 sm:p-6 lg:p-8 border-b border-border/20 bg-gradient-to-r from-primary/5 to-primary-glow/5 text-center sm:text-left">
                <GradientHeading size="xl" className="mb-1 sm:mb-2 leading-tight tracking-tight">
                  {showingAIResults ? `Suchergebnisse${searchQuery ? `: "${searchQuery}"` : ''}` : 'Aktuelle Stellenangebote'}
                </GradientHeading>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {showingAIResults 
                    ? `${aiMatchedJobsFromState?.length} passende ${aiMatchedJobsFromState?.length === 1 ? 'Position' : 'Positionen'} gefunden`
                    : 'Durchsuchen Sie unsere aktuellen Positionen und bewerben Sie sich direkt online'
                  }
                </p>
              </div>
            </div>
            
            <JobList 
              aiMatchedJobs={showingAIResults ? aiMatchedJobsFromState : null} 
              onClearAIResults={() => setShowingAIResults(false)}
              aiReasoning={showingAIResults ? reasoning : undefined}
              aiConfidence={showingAIResults ? confidence : undefined}
              showAISearch={showAISearchFromState}
            />
            <JobSitemap />
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default Candidates;